import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Rss, Plus, Pencil, Trash2, Image as ImageIcon,
    Calendar, AlertTriangle, Facebook, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
    listPagesConfig, deletePageConfig, type SocialPageConfig,
    listCategoryConfigs, deleteCategoryConfig, type SocialCategoryConfig,
} from '@/services/api/social-publisher';
import PageConfigForm from './components/PageConfigForm';
import CategoryConfigForm from './components/CategoryConfigForm';


function StatusBadge({ status }: { status: string }) {
    const variant: Record<string, string> = {
        active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        paused: 'bg-amber-100 text-amber-700 border-amber-200',
        archived: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full border ${variant[status] || variant.archived}`}>
            {status}
        </span>
    );
}


export default function SocialPublisher() {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [tab, setTab] = useState<'categories' | 'pages'>('categories');
    const [editingPage, setEditingPage] = useState<SocialPageConfig | null>(null);
    const [pageFormOpen, setPageFormOpen] = useState(false);
    const [editingCat, setEditingCat] = useState<SocialCategoryConfig | null>(null);
    const [catFormOpen, setCatFormOpen] = useState(false);

    const { data: configs = [], isLoading } = useQuery({
        queryKey: ['social-publisher', 'pages-config'],
        queryFn: () => listPagesConfig(),
        refetchInterval: 1000 * 60,
    });
    const { data: catConfigs = [], isLoading: loadingCats } = useQuery({
        queryKey: ['social-publisher', 'category-configs'],
        queryFn: listCategoryConfigs,
        refetchInterval: 1000 * 60,
    });

    // Filtra pages_config que são só "espelho" de category_config
    // (persona_name NULL = herdado). Deixamos elas invisíveis no hub por
    // enquanto pra evitar duplicação — o detail da categoria mostra tudo.
    const realPageConfigs = configs.filter((c) => c.persona_name);

    const deletePageMut = useMutation({
        mutationFn: (id: number) => deletePageConfig(id),
        onSuccess: () => {
            toast({ title: 'Removida' });
            queryClient.invalidateQueries({ queryKey: ['social-publisher', 'pages-config'] });
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });
    const deleteCatMut = useMutation({
        mutationFn: (id: number) => deleteCategoryConfig(id),
        onSuccess: () => {
            toast({ title: 'Removida' });
            queryClient.invalidateQueries({ queryKey: ['social-publisher', 'category-configs'] });
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-br from-fuchsia-600 to-purple-700 rounded-2xl p-6 text-white flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="bg-white/15 p-3 rounded-xl">
                        <Rss className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Social Publisher</h1>
                        <p className="text-sm text-white/75">
                            RSS → IA → Facebook (posts + stories automáticos)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/meta/social-publisher/feeds">
                        <Button variant="secondary" className="bg-white/15 text-white hover:bg-white/25 border-0">
                            <Rss className="w-4 h-4 mr-2" /> Feeds RSS
                        </Button>
                    </Link>
                    <Link to="/meta/social-publisher/assets">
                        <Button variant="secondary" className="bg-white/15 text-white hover:bg-white/25 border-0">
                            <ImageIcon className="w-4 h-4 mr-2" /> Mídia
                        </Button>
                    </Link>
                    <Button
                        onClick={() => {
                            if (tab === 'categories') {
                                setEditingCat(null); setCatFormOpen(true);
                            } else {
                                setEditingPage(null); setPageFormOpen(true);
                            }
                        }}
                        className="bg-white text-purple-700 hover:bg-white/90 font-medium"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {tab === 'categories' ? 'Configurar categoria' : 'Configurar página'}
                    </Button>
                </div>
            </div>

            {/* Como funciona */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                    <p className="font-medium mb-1">Como funciona</p>
                    <ul className="list-disc list-inside space-y-0.5 text-amber-800 text-xs">
                        <li><strong>Categoria (recomendado)</strong> — 1 config → gera pra todas as pages daquela categoria com feeds vinculados</li>
                        <li><strong>Página</strong> — override individual pra customizar UMA page específica</li>
                        <li>Cadastre feeds RSS em <em>Feeds RSS</em> e vincule à categoria (ou à página)</li>
                    </ul>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
                <TabsList>
                    <TabsTrigger value="categories" className="gap-2">
                        <Layers className="w-4 h-4" /> Categorias
                        {catConfigs.length > 0 && (
                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 rounded-full">
                                {catConfigs.length}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="pages" className="gap-2">
                        <Facebook className="w-4 h-4" /> Páginas (override)
                        {realPageConfigs.length > 0 && (
                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 rounded-full">
                                {realPageConfigs.length}
                            </span>
                        )}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="categories" className="mt-4">
                    {loadingCats ? (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">
                            Carregando...
                        </div>
                    ) : catConfigs.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                            <Layers className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm font-medium text-gray-600 mb-1">Nenhuma categoria configurada</p>
                            <p className="text-xs text-gray-400 mb-4">
                                Configure uma categoria e todas as pages dela herdam a persona.
                            </p>
                            <Button
                                size="sm"
                                onClick={() => { setEditingCat(null); setCatFormOpen(true); }}
                            >
                                <Plus className="w-4 h-4 mr-2" /> Configurar primeira categoria
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {catConfigs.map((cfg) => (
                                <div
                                    key={cfg.id}
                                    className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                            <Layers className="w-5 h-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-gray-800 truncate">
                                                {cfg.category_name || `Categoria #${cfg.category_id}`}
                                            </div>
                                            <div className="text-[11px] text-purple-600">
                                                {cfg.valid_pages_count ?? 0} pages ativas ·{' '}
                                                {(cfg.feed_ids || []).length} feeds
                                            </div>
                                        </div>
                                        <StatusBadge status={cfg.status} />
                                    </div>

                                    <div className="space-y-1.5 text-xs text-gray-600 mb-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-purple-500" />
                                            <span>Posts: {(cfg.post_schedule_times || []).join(', ') || '—'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-fuchsia-500" />
                                            <span>Stories: {(cfg.story_schedule_times || []).join(', ') || '—'}</span>
                                        </div>
                                        <div className="text-[11px] text-gray-400">
                                            Horizonte {cfg.horizon_days}d · max {cfg.max_per_day}/dia/page
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                                        <Link to={`/meta/social-publisher/categorias/${cfg.id}`} className="flex-1">
                                            <Button size="sm" variant="outline" className="w-full">
                                                Abrir
                                            </Button>
                                        </Link>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => { setEditingCat(cfg); setCatFormOpen(true); }}
                                            title="Editar"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => {
                                                if (confirm('Remover essa configuração de categoria? Pages herdadas voltam a não ter config.')) {
                                                    deleteCatMut.mutate(cfg.id);
                                                }
                                            }}
                                            title="Remover"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="pages" className="mt-4">
                    <div className="text-xs text-gray-500 mb-3">
                        Configurações individuais que <strong>sobrescrevem</strong> a config da categoria pra uma page específica.
                    </div>
                    {isLoading ? (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">
                            Carregando...
                        </div>
                    ) : realPageConfigs.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                            <Facebook className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm font-medium text-gray-600 mb-1">Nenhum override individual</p>
                            <p className="text-xs text-gray-400 mb-4">
                                Se uma page precisa de persona/horários diferentes da categoria, crie override aqui.
                            </p>
                            <Button
                                size="sm"
                                onClick={() => { setEditingPage(null); setPageFormOpen(true); }}
                            >
                                <Plus className="w-4 h-4 mr-2" /> Criar override
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {realPageConfigs.map((cfg) => (
                                <div
                                    key={cfg.id}
                                    className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        {cfg.page_picture_url ? (
                                            <img
                                                src={cfg.page_picture_url}
                                                alt={cfg.page_name || ''}
                                                className="w-12 h-12 rounded-full object-cover border-2 border-purple-100"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                                                <Facebook className="w-5 h-5" />
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-gray-800 truncate">
                                                {cfg.page_name || `page_pk #${cfg.page_pk}`}
                                            </div>
                                            {cfg.persona_name && (
                                                <div className="text-[11px] text-purple-600 truncate">
                                                    🎭 {cfg.persona_name}
                                                </div>
                                            )}
                                        </div>
                                        <StatusBadge status={cfg.status} />
                                    </div>
                                    <div className="space-y-1.5 text-xs text-gray-600 mb-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-purple-500" />
                                            <span>Posts: {(cfg.post_schedule_times || []).join(', ') || '—'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-3.5 h-3.5 text-fuchsia-500" />
                                            <span>Stories: {(cfg.story_schedule_times || []).join(', ') || '—'}</span>
                                        </div>
                                        <div className="text-[11px] text-gray-400">
                                            Horizonte {cfg.horizon_days}d · max {cfg.max_per_day}/dia · {cfg.schedule_timezone}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                                        <Link to={`/meta/social-publisher/configs/${cfg.id}`} className="flex-1">
                                            <Button size="sm" variant="outline" className="w-full">
                                                Abrir
                                            </Button>
                                        </Link>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => { setEditingPage(cfg); setPageFormOpen(true); }}
                                            title="Editar"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => {
                                                if (confirm('Remover esse override? Page volta a herdar da categoria.')) {
                                                    deletePageMut.mutate(cfg.id);
                                                }
                                            }}
                                            title="Remover"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <PageConfigForm
                open={pageFormOpen}
                onOpenChange={setPageFormOpen}
                editing={editingPage}
                onSaved={() => {
                    setPageFormOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['social-publisher', 'pages-config'] });
                }}
            />
            <CategoryConfigForm
                open={catFormOpen}
                onOpenChange={setCatFormOpen}
                editing={editingCat}
                onSaved={() => {
                    setCatFormOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['social-publisher', 'category-configs'] });
                }}
            />
        </div>
    );
}
